import { ZERO_WEI } from '@kwenta/sdk/constants'
import { FuturesMarketAsset, OrderTypeEnum, PositionSide } from '@kwenta/sdk/types'
import { formatNumber, getDisplayAsset, stripZeros, suggestedDecimals } from '@kwenta/sdk/utils'
import { wei } from '@kwenta/wei'

import { type FC, memo, useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'

import TextButton from 'components/Button/TextButton'
import InputTitle from 'components/Input/InputTitle'
import NumericInput from 'components/Input/NumericInput'
import Select from 'components/Select'
import StyledSlider from 'components/Slider/StyledSlider'
import Spacer from 'components/Spacer'
import { Body } from 'components/Text'
import { FlexDiv, FlexDivRow } from 'components/layout/flex'
import { selectShowPositionModal } from 'state/app/selectors'
import { editPositionOrderPrice, editPositionSize } from 'state/futures/actions'
import { selectEditPositionInputs } from 'state/futures/common/selectors'
import { setEditPositionInputs } from 'state/futures/reducer'
import { selectEditPositionModalInfo } from 'state/futures/selectors'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { orderPriceInvalidLabel } from 'utils/futures'

type OrderSizingProps = {
	type: 'increase' | 'decrease'
	maxNativeValue: string
	minNativeValue: string
	isMobile?: boolean
	priceInvalid: boolean
	minusSizeDelta: boolean
}

type OrderType = OrderTypeEnum.MARKET | OrderTypeEnum.LIMIT

const EditPositionSizeInput: FC<OrderSizingProps> = memo(
	({ isMobile, type, maxNativeValue, minNativeValue, priceInvalid, minusSizeDelta }) => {
		const dispatch = useAppDispatch()

		const { nativeSizeDelta, orderPrice, orderType } = useAppSelector(selectEditPositionInputs)
		const { position, indexPrice } = useAppSelector(selectEditPositionModalInfo)
		const modal = useAppSelector(selectShowPositionModal)
		const [value, setValue] = useState<number>(0)

		useEffect(() => {
			if (nativeSizeDelta === '') setValue(0)
		}, [nativeSizeDelta])
		const onSizeChange = useCallback(
			(value: string) => {
				if (modal) {
					const sizeDelta = minusSizeDelta ? `-${value}` : value
					dispatch(editPositionSize(sizeDelta))
				}
				setValue((Number(value) / Number(maxNativeValue)) * 100)
			},
			[dispatch, modal, minusSizeDelta, maxNativeValue]
		)

		const onPriceChange = useCallback(
			(value: string) => {
				if (modal) {
					dispatch(editPositionOrderPrice(value))
				}
			},
			[dispatch, modal]
		)

		const onPriceTypeChange = useCallback(
			(value: OrderType) => {
				dispatch(
					setEditPositionInputs({
						orderPrice: '',
						orderType: value,
					})
				)
			},
			[dispatch]
		)

		const handleSetMax = useCallback(() => {
			onSizeChange(stripZeros(maxNativeValue))
		}, [onSizeChange, maxNativeValue])

		const onChangeValue = useCallback(
			(_: any, v: string) => {
				onSizeChange(v)
			},
			[onSizeChange]
		)

		const nativeSizeDeltaWei = useMemo(() => {
			return !nativeSizeDelta || Number.isNaN(Number(nativeSizeDelta))
				? ZERO_WEI
				: wei(nativeSizeDelta)
		}, [nativeSizeDelta])

		const maxNativeValueWei = useMemo(() => {
			return wei(maxNativeValue)
		}, [maxNativeValue])

		const minNativeValueWei = useMemo(() => {
			return wei(minNativeValue)
		}, [minNativeValue])

		const onSelectSizePercent = useCallback(
			(percent: number) => {
				if (!position?.details?.size || !position.market?.asset) return
				const percentage = percent / 100
				const size =
					percentage === 1
						? maxNativeValueWei
						: maxNativeValueWei.sub(minNativeValueWei).mul(percentage).add(minNativeValueWei)
				const decimals = size.abs().eq(wei(position.details.size).abs()) ? undefined : 4

				if (modal) {
					const sizeDelta = minusSizeDelta ? size.neg() : size
					dispatch(editPositionSize(stripZeros(sizeDelta.toString(decimals))))
				}
			},
			[
				dispatch,
				minusSizeDelta,
				modal,
				maxNativeValueWei,
				minNativeValueWei,
				position?.details?.size,
				position?.market?.asset,
			]
		)

		const maxNativeValueWithBuffer = useMemo(() => {
			if (type === 'decrease') return maxNativeValueWei
			return maxNativeValueWei.add(maxNativeValueWei.mul(0.001))
		}, [maxNativeValueWei, type])

		const sizeInvalid =
			type === 'increase' &&
			nativeSizeDelta !== '' &&
			(maxNativeValueWithBuffer.lt(nativeSizeDeltaWei.abs()) ||
				wei(minNativeValue).gt(nativeSizeDeltaWei.abs()))

		const formatOptionLabel = ({ label }: any) => {
			return <StyledLabelContainer>{label}</StyledLabelContainer>
		}

		const minMaxLabelString = useMemo(
			() =>
				orderPriceInvalidLabel(
					orderPrice,
					minusSizeDelta ? PositionSide.SHORT : PositionSide.LONG,
					(indexPrice ?? ZERO_WEI).toString(),
					OrderTypeEnum.LIMIT
				),
			[orderPrice, minusSizeDelta, indexPrice]
		)
		return (
			<OrderSizingContainer>
				<OrderSizingRow>
					<InputTitle>{type === 'increase' ? 'Increase' : 'Reduce'} position size</InputTitle>
					<StyledInputTitle>
						{minMaxLabelString && <span> {minMaxLabelString}</span>}
					</StyledInputTitle>
					<div>
						<TextButton onClick={handleSetMax}>Max</TextButton>
						<Body type="span">{`${Number(maxNativeValueWei.toString(suggestedDecimals(maxNativeValueWei)))} ${getDisplayAsset(position?.market?.asset)}`}</Body>
					</div>
				</OrderSizingRow>

				<FlexDivRow columnGap="8px">
					<NumericInput
						invalid={sizeInvalid}
						dataTestId={`edit-position-size-input${isMobile ? '-mobile' : '-desktop'}`}
						value={nativeSizeDelta.replace('-', '')}
						placeholder="0.00"
						onChange={onChangeValue}
						height={48}
					/>
					<PriceInput
						invalid={priceInvalid}
						dataTestId={`set-order-size-amount-susd${isMobile ? '-mobile' : '-desktop'}`}
						disabled={orderType === OrderTypeEnum.MARKET}
						right={
							<StyledSelect
								variant="flat"
								onChange={(option: { value: OrderType }) => onPriceTypeChange(option.value)}
								formatOptionLabel={formatOptionLabel}
								options={[
									{ value: OrderTypeEnum.MARKET, label: 'Market' },
									{ value: OrderTypeEnum.LIMIT, label: 'Limit' },
								]}
								defaultValue={{ value: OrderTypeEnum.MARKET, label: 'Market' }}
							/>
						}
						value={
							orderType === OrderTypeEnum.LIMIT
								? orderPrice
								: formatNumber(indexPrice ?? 0, {
										suggestDecimalsForAsset: position?.market?.asset ?? FuturesMarketAsset.sETH,
									})
						}
						placeholder="0.00"
						height={48}
						onChange={(_, v) => onPriceChange(v)}
					/>
				</FlexDivRow>
				<Spacer height={10} />
				<StyledSlider
					minValue={0}
					maxValue={100}
					step={1}
					defaultValue={0}
					value={value}
					onChange={(_, newValue) => {
						setValue(newValue as number)
						onSelectSizePercent(newValue as number)
					}}
					valueLabelFormat={(v: number) => `${v}%`}
					$currentMark={Number(nativeSizeDelta ?? 0)}
					marks={[
						{ value: 0, label: '0' },
						{ value: 25, label: '25%' },
						{ value: 50, label: '50%' },
						{ value: 75, label: '75%' },
						{ value: 100, label: '100%' },
					]}
				/>
			</OrderSizingContainer>
		)
	}
)

const OrderSizingContainer = styled.div`
`

const OrderSizingRow = styled(FlexDivRow)`
	width: 100%;
	align-items: center;
	margin-bottom: 8px;
	cursor: default;
`

const PriceInput = styled(NumericInput)<{ disabled?: boolean }>`
	padding-right: 0;
	> input {
	 	opacity: ${(props) => (props.disabled ? 0.4 : 1)};
	}
	opacity: 1;
`

const StyledLabelContainer = styled(FlexDiv)`
	justify-content: flex-start;
	gap: 5px;
	color: ${(props) => props.theme.colors.selectedTheme.text.value};
`

export const StyledSelect = styled(Select)`
	width: 88px;
	z-index: 100;
	.react-select__control {
		border-right: none;
		border-top: none;
		border-bottom: none;
		border-radius: 0;
		padding: 5.5px 2px;
		background-color: transparent;
	}

	.react-select__menu {
		z-index: 10;
		width: 100%;
		background-color: ${(props) =>
			props.theme.colors.selectedTheme.newTheme.containers.secondary.background};
	}
`

const StyledInputTitle = styled(InputTitle)`
	text-transform: capitalize;
	span {
		color: ${(props) => props.theme.colors.selectedTheme.red};
	}
`

export default EditPositionSizeInput
